/**
 * TrustArc consent helpers.
 *
 * Single source of truth (client side) for reading the visitor's consent from
 * TrustArc's `notice_gdpr_prefs` cookie, which lists the exact set of consented
 * category IDs. On this site: 0 = Required, 1 = Functional, 2 = Advertising
 * (functional only => "0,1", advertising only => "0,2", all => "0,1,2").
 *
 * The HubSpot consent bridge in functions/hubspot.php reads the same cookie for
 * its own (inline, pre-bundle) context — keep the category IDs in sync.
 */

/** TrustArc category IDs. */
export const CONSENT_CATEGORY = {
	REQUIRED: 0,
	FUNCTIONAL: 1,
	ADVERTISING: 2,
};

/**
 * The set of consented category IDs.
 *
 * @returns {number[]|null} Consented category IDs, or null if the visitor has
 *   not made a choice yet (no cookie).
 */
export function getConsentedCategories() {
	const match = document.cookie.match(/(?:^|;\s*)notice_gdpr_prefs=([^;]*)/);
	if (!match) {
		return null;
	}
	const value = decodeURIComponent(match[1]).split(':')[0];
	if (value === '') {
		return [];
	}
	return value
		.split(',')
		.map((id) => parseInt(id, 10))
		.filter((id) => !Number.isNaN(id));
}

/**
 * Whether the visitor has consented to a given category.
 *
 * @param {number} category Category ID (see CONSENT_CATEGORY).
 * @returns {boolean}
 */
export function hasConsent(category) {
	const categories = getConsentedCategories();
	return !!categories && categories.indexOf(category) !== -1;
}

/**
 * Whether functional consent has been granted.
 *
 * @returns {boolean}
 */
export function hasFunctionalConsent() {
	return hasConsent(CONSENT_CATEGORY.FUNCTIONAL);
}
